import React from 'react';
import { FD130 } from '../../../services/d203Api';
import { dateToInput, inputToDate, fmt, PROCESSING_CATEGORY, PRICING_METHOD, MATERIAL_CATEGORY } from '../helpers';

interface Tab1Props {
  formData: Partial<FD130>;
  names: Record<string, string>;
  isEditing: boolean;
  isFieldDisabled: (key: string) => boolean;
  getField: (key: keyof FD130) => FD130[keyof FD130];
  setField: (key: keyof FD130, value: FD130[keyof FD130]) => void;
  resolveSingleName: (nameKey: string, type: string, code: string) => void;
  getFieldError?: (field: string) => string | undefined;
}

const Tab1BasicInfo: React.FC<Tab1Props> = React.memo(({
  formData, names, isEditing, isFieldDisabled,
  getField, setField, resolveSingleName, getFieldError,
}) => {
  const E = (field: string) => {
    const err = getFieldError?.(field);
    return err ? <span style={{ color: '#ff4d4f', fontSize: 11, marginLeft: 6 }}>{err}</span> : null;
  };

  return (
    <>
      {/* Section 1: 基本資料 */}
      <div className="form-section odd">
        <span className="section-tag">基本資料</span>
        <div className="form-grid">
          <label>託工單號</label>
          <input type="text" readOnly style={{ maxWidth: 140 }} value={getField('fd130_01') as string} data-contract-field="fd130_01" />

          <label>託工日期{E('fd130_11')}</label>
          <input type="date" style={{ maxWidth: 160 }}
            disabled={isFieldDisabled('fd130_11')}
            value={dateToInput(getField('fd130_11') as string)}
            onChange={e => setField('fd130_11', inputToDate(e.target.value) as string)} data-contract-field="fd130_11"
          />

          <label>預定交期{E('fd130_13')}</label>
          <input type="date" style={{ maxWidth: 160 }}
            disabled={isFieldDisabled('fd130_13')}
            value={dateToInput(getField('fd130_13') as string)}
            onChange={e => setField('fd130_13', inputToDate(e.target.value) as string)} data-contract-field="fd130_13"
          />

          <label>加工廠{E('fd130_12')}</label>
          <div className="lookup-field">
            <input type="text" style={{ width: 100 }} maxLength={8}
              disabled={isFieldDisabled('fd130_12')}
              value={getField('fd130_12') as string}
              onChange={e => setField('fd130_12', e.target.value)}
              onBlur={() => resolveSingleName('processor', 'company', getField('fd130_12') as string)} data-contract-field="fd130_12"
            />
            <span className="lookup-name">{names.processor || '--'}</span>
          </div>

          <label>S/C NO</label>
          <input type="text" style={{ maxWidth: 120 }}
            disabled={isFieldDisabled('fd130_10')}
            value={getField('fd130_10') as string}
            onChange={e => setField('fd130_10', e.target.value)} data-contract-field="fd130_10"
          />

          <label>S/C 項次</label>
          <input type="number" style={{ maxWidth: 80 }}
            disabled={isFieldDisabled('fd130_101')}
            value={getField('fd130_101') as number || ''}
            onChange={e => setField('fd130_101', parseInt(e.target.value) || 0)} data-contract-field="fd130_101"
          />

          <label>入單人員</label>
          <div className="lookup-field">
            <input type="text" readOnly style={{ width: 100 }} value={getField('fd130_33') as string} data-contract-field="fd130_33" />
            <span className="lookup-name">{names.entryPerson || '--'}</span>
          </div>

          <label>工繳代號</label>
          <div className="lookup-field">
            <input type="text" style={{ width: 100 }} maxLength={8}
              disabled={isFieldDisabled('fd130_15')}
              value={getField('fd130_15') as string}
              onChange={e => setField('fd130_15', e.target.value)}
              onBlur={() => resolveSingleName('processingFee', 'processingFeeCode', getField('fd130_15') as string)} data-contract-field="fd130_15"
            />
            <span className="lookup-name">{names.processingFee || '--'}</span>
          </div>

          <label>預估縮率%</label>
          <input type="number" style={{ maxWidth: 100 }}
            disabled={isFieldDisabled('fd130_14')}
            value={getField('fd130_14') as number || ''}
            onChange={e => setField('fd130_14', parseFloat(e.target.value) || 0)} data-contract-field="fd130_14"
          />
        </div>
      </div>

      {/* Section 2: 計價與幣別 */}
      <div className="form-section even">
        <span className="section-tag">計價與幣別</span>
        <div className="form-grid">
          <label>計價方式</label>
          <div className="radio-group">
            {Object.entries(PRICING_METHOD).map(([val, label]) => (
              <label key={val}>
                <input type="radio" name="fd130_40"
                  disabled={isFieldDisabled('fd130_40')}
                  checked={(getField('fd130_40') as number) === Number(val)}
                  onChange={() => setField('fd130_40', Number(val))} data-contract-field="fd130_40"
                />
                {label}
              </label>
            ))}
          </div>

          <label>幣別</label>
          <div className="lookup-field">
            <input type="text" style={{ width: 80 }} maxLength={6}
              disabled={isFieldDisabled('fd130_45')}
              value={getField('fd130_45') as string}
              onChange={e => setField('fd130_45', e.target.value)}
              onBlur={() => resolveSingleName('currency', 'currency', getField('fd130_45') as string)} data-contract-field="fd130_45"
            />
            <span className="lookup-name">{names.currency || '--'}</span>
          </div>

          <label>匯率</label>
          <input type="number" style={{ maxWidth: 100 }} step="0.0001"
            disabled={isFieldDisabled('fd130_46')}
            value={getField('fd130_46') as number || ''}
            onChange={e => setField('fd130_46', parseFloat(e.target.value) || 0)} data-contract-field="fd130_46"
          />

          <label>交易條件</label>
          <div className="lookup-field">
            <input type="text" style={{ width: 80 }} maxLength={4}
              disabled={isFieldDisabled('fd130_47')}
              value={getField('fd130_47') as string}
              onChange={e => setField('fd130_47', e.target.value)}
              onBlur={() => resolveSingleName('tradeTerms', 'tradeTerms', getField('fd130_47') as string)} data-contract-field="fd130_47"
            />
            <span className="lookup-name">{names.tradeTerms || '--'}</span>
          </div>

          <label>付款條件</label>
          <div className="lookup-field">
            <input type="text" style={{ width: 80 }} maxLength={4}
              disabled={isFieldDisabled('fd130_471')}
              value={getField('fd130_471') as string}
              onChange={e => setField('fd130_471', e.target.value)}
              onBlur={() => resolveSingleName('paymentTerms', 'paymentTerms', getField('fd130_471') as string)} data-contract-field="fd130_471"
            />
            <span className="lookup-name">{names.paymentTerms || '--'}</span>
          </div>

          <label>包裝代號</label>
          <div className="lookup-field">
            <input type="text" style={{ width: 80 }} maxLength={4}
              disabled={isFieldDisabled('fd130_16a')}
              value={getField('fd130_16a') as string}
              onChange={e => setField('fd130_16a', e.target.value)}
              onBlur={() => resolveSingleName('packing', 'packing', getField('fd130_16a') as string)} data-contract-field="fd130_16a"
            />
            <span className="lookup-name">{names.packing || '--'}</span>
          </div>

          <label>工繳合計金額</label>
          <input type="text" readOnly style={{ maxWidth: 140, textAlign: 'right' }}
            value={fmt(formData.fd130_34)} data-contract-field="fd130_34"
          />
        </div>
      </div>

      {/* Section 3: 加工類別 */}
      <div className="form-section odd">
        <span className="section-tag">加工與分類</span>
        <div className="form-grid">
          <label>加工類別</label>
          <div className="radio-group">
            {Object.entries(PROCESSING_CATEGORY).map(([val, label]) => (
              <label key={val}>
                <input type="radio" name="fd130_48"
                  disabled={isFieldDisabled('fd130_48')}
                  checked={(getField('fd130_48') as number) === Number(val)}
                  onChange={() => setField('fd130_48', Number(val))} data-contract-field="fd130_48"
                />
                {label}
              </label>
            ))}
          </div>

          <label>原料/半成品/成品</label>
          <div className="radio-group">
            {Object.entries(MATERIAL_CATEGORY).map(([val, label]) => (
              <label key={val}>
                <input type="radio" name="fd130_31"
                  disabled={isFieldDisabled('fd130_31')}
                  checked={(getField('fd130_31') as number) === Number(val)}
                  onChange={() => setField('fd130_31', Number(val))} data-contract-field="fd130_31"
                />
                {label}
              </label>
            ))}
          </div>

          <label>單位</label>
          <div className="lookup-field">
            <input type="text" style={{ width: 80 }} maxLength={4}
              disabled={isFieldDisabled('fd130_19')}
              value={getField('fd130_19') as string}
              onChange={e => setField('fd130_19', e.target.value)}
              onBlur={() => resolveSingleName('unit', 'unit', getField('fd130_19') as string)} data-contract-field="fd130_19"
            />
            <span className="lookup-name">{names.unit || '--'}</span>
          </div>

          <label>Q.C 人員</label>
          <div className="lookup-field">
            <input type="text" style={{ width: 100 }} maxLength={8}
              disabled={isFieldDisabled('fd130_27')}
              value={getField('fd130_27') as string}
              onChange={e => setField('fd130_27', e.target.value)}
              onBlur={() => resolveSingleName('qcPerson', 'employee', getField('fd130_27') as string)} data-contract-field="fd130_27"
            />
            <span className="lookup-name">{names.qcPerson || '--'}</span>
          </div>

          <label>樣布需要量</label>
          <div className="lookup-field">
            <input type="text" style={{ width: 120 }} maxLength={20}
              disabled={isFieldDisabled('fd130_35')}
              value={getField('fd130_35') as string}
              onChange={e => setField('fd130_35', e.target.value)}
              onBlur={() => resolveSingleName('sampleCloth', 'sampleCloth', getField('fd130_35') as string)} data-contract-field="fd130_35"
            />
            <span className="lookup-name">{names.sampleCloth || '--'}</span>
          </div>

          <label>入布廠商</label>
          <input type="text" style={{ maxWidth: 140 }} maxLength={20}
            disabled={isFieldDisabled('fd130_36')}
            value={getField('fd130_36') as string}
            onChange={e => setField('fd130_36', e.target.value)} data-contract-field="fd130_36"
          />

          <label>
            <span>開立三聯式發票</span>
          </label>
          <div className="checkbox-field">
            <input type="checkbox"
              disabled={isFieldDisabled('fd130_52')}
              checked={!!getField('fd130_52')}
              onChange={e => setField('fd130_52', e.target.checked)} data-contract-field="fd130_52"
            />
          </div>
        </div>
      </div>
    </>
  );
});

Tab1BasicInfo.displayName = 'Tab1BasicInfo';
export default Tab1BasicInfo;
